/**
 * OverrideField -- Shows a base value and an override input for personal info.
 *
 * Displays the field label, the base value (grayed out), and an override input.
 * Includes a clear button to remove the override and revert to base.
 */
import { useState, useEffect } from 'react';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';

export interface OverrideFieldProps {
  fieldName: string;
  label: string;
  baseValue: string | null;
  overrideValue: string | null;
  onSave: (fieldName: string, value: string) => void;
  onClear: (fieldName: string) => void;
}

export function OverrideField({
  fieldName,
  label,
  baseValue,
  overrideValue,
  onSave,
  onClear,
}: OverrideFieldProps) {
  const [localValue, setLocalValue] = useState(overrideValue ?? '');
  const hasOverride = overrideValue !== null;

  useEffect(() => {
    setLocalValue(overrideValue ?? '');
  }, [overrideValue]);

  function handleBlur() {
    const trimmed = localValue.trim();
    if (trimmed && trimmed !== overrideValue) {
      onSave(fieldName, trimmed);
    }
  }

  function handleClear() {
    setLocalValue('');
    onClear(fieldName);
  }

  return (
    <div className="override-field">
      <div className="override-field__base">
        <span className="override-field__label">{label}</span>
        <span className="override-field__base-value">
          {baseValue ?? '(not set)'}
        </span>
      </div>
      <div className="override-field__input-row">
        <Input
          label={`Override ${label}`}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          placeholder={baseValue ?? `Enter ${label.toLowerCase()}`}
        />
        {hasOverride && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClear}
            aria-label={`Clear ${label} override`}
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}
